import React from 'react';
import { useTranslation } from 'react-i18next';

// eslint-disable-next-line import/named
import { Typography, type TypographyTypeMap } from '@mui/material';

import TermsAndPoliciesFooter from '../../../general/termsAndPolicies/TermsAndPoliciesFooter';
import {
    IconContainer,
    StyledMessageWithTipContainer,
    StyledWidthWrapper,
} from './Styles';

type descriptionType = string | React.ReactElement | null;

interface MessageWithTipProps {
    headline: string;
    headlineVariant?: TypographyTypeMap['props']['variant'];
    headlineAlignment?: TypographyTypeMap['props']['align'];
    description: string | React.ReactElement | null;
    descriptionAlignment?: TypographyTypeMap['props']['align'];
    tip?: string;
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    Icon?: React.FC<any>;
    iconDescription?: string | null;
    footerSlot?: JSX.Element;
    secondDescription?: descriptionType;
    thirdDescription?: descriptionType;
    termsFooter?: boolean;
}

export const MessageWithTipMui = ({
    headline,
    headlineVariant = 'h1',
    headlineAlignment = 'left',
    description,
    descriptionAlignment = 'left',
    tip,
    Icon,
    iconDescription,
    footerSlot,
    secondDescription,
    thirdDescription,
    termsFooter,
}: MessageWithTipProps): JSX.Element => {
    const { t } = useTranslation();

    const renderDescription = () => {
        if (!description) {
            return null;
        }

        if (
            typeof description === 'string' &&
            typeof secondDescription === 'string'
        ) {
            return (
                <>
                    <Typography variant="body1" align={descriptionAlignment}>
                        {description}
                    </Typography>
                    <Typography variant="body1" align={descriptionAlignment}>
                        {secondDescription}
                    </Typography>
                    {thirdDescription && (
                        <Typography variant="body1">
                            {thirdDescription}
                        </Typography>
                    )}
                </>
            );
        }

        if (typeof description === 'string') {
            return (
                <Typography align={descriptionAlignment} variant="body1">
                    {description}
                </Typography>
            );
        }

        return description;
    };

    return (
        <StyledMessageWithTipContainer>
            <StyledWidthWrapper
                style={{ marginBottom: (tip || footerSlot) && '47px' }}
            >
                {(Icon || iconDescription) && (
                    <IconContainer>
                        {Icon && <Icon style={{ margin: '0 0 25px 0' }} />}
                        {iconDescription && (
                            <Typography
                                variant="caption"
                                sx={{ color: '#999999' }}
                                margin="0px 10px 25px"
                            >
                                {iconDescription}
                            </Typography>
                        )}
                    </IconContainer>
                )}
                <Typography
                    align={headlineAlignment}
                    variant={headlineVariant}
                    marginBottom={'15px'}
                >
                    {headline}
                </Typography>
                {renderDescription()}
            </StyledWidthWrapper>
            {tip && (
                <StyledWidthWrapper>
                    <Typography variant="caption" margin={0}>
                        {t('General.Tip.Headline')}
                    </Typography>
                    <br />
                    <Typography variant="caption" style={{ color: '#999999' }}>
                        {tip}
                    </Typography>
                </StyledWidthWrapper>
            )}
            {footerSlot}
            {termsFooter && <TermsAndPoliciesFooter />}
        </StyledMessageWithTipContainer>
    );
};
